import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

export function useChildWindow(props: {
  width: number;
  height: number;
  onClose?: () => void;
}) {
  const [childWindow, setChildWindow] = useState<Window | undefined>(undefined);

  useEffect(() => {
    if (!childWindow) {
      return;
    }

    const handleParentWindowBeforeUnload = () => {
      childWindow.close();
    };

    const handleChildWindowUnload = () => {
      setChildWindow(undefined);
    };

    window.addEventListener("beforeunload", handleParentWindowBeforeUnload);
    childWindow.addEventListener("unload", handleChildWindowUnload);

    return () => {
      window.removeEventListener(
        "beforeunload",
        handleParentWindowBeforeUnload,
      );

      childWindow.removeEventListener("unload", handleChildWindowUnload);
    };
  }, [childWindow]);

  useEffect(() => {
    const onClose = props.onClose;
    if (!childWindow || !onClose) {
      return;
    }

    childWindow.addEventListener("unload", onClose);
    return () => {
      childWindow.removeEventListener("unload", onClose);
    };
  }, [childWindow, props.onClose]);

  const handleUnmount = useEffectEvent(() => {
    childWindow?.close();
  });

  useEffect(() => () => handleUnmount(), [handleUnmount]);

  const open = useEffectEvent(() => {
    if (childWindow) {
      childWindow.focus();
      return;
    }

    const newChildWindow = openChildWindow(props);
    if (!newChildWindow) {
      throw new Error("Failed to open child window");
    }

    // TODO: Handle concurrent calls to open() and handle errors.
    newChildWindow.addEventListener("load", () => {
      // Hide the /childWindow.html path from the address bar without affecting the loaded document
      newChildWindow.history.replaceState(null, "", "/");
      setChildWindow(newChildWindow);
    });
  });

  const close = useEffectEvent(() => {
    childWindow?.close();
  });

  const render = useCallback(
    (children: ReactNode) => {
      if (!childWindow) {
        return null;
      }

      return createPortal(children, childWindow.document.body);
    },
    [childWindow],
  );

  return { render, open, close };
}

function openChildWindow(props: { width: number; height: number }) {
  const childWindow = window.open(
    // Load an HTML file so that the base URL, stylesheets, HMR, etc. just work
    "/childWindow.html",
    "",
    `width=${props.width.toFixed(0)},height=${props.height.toFixed(0)}`,
  );

  if (!childWindow) {
    console.error("Failed to open child window");
    return null;
  }

  // We don't need the child window to access window.opener, so let's clear it to avoid it from every being abused
  childWindow.opener = null;
  return childWindow;
}
